/**
 * Atom Recipe Generation
 *
 * Generates atom rendering recipes with pre-calculated display symbols and metadata.
 * Follows the same pattern as bondRecipes.ts for consistency.
 */

import { AtomContainer, IAtomModel } from '@alchemiesolns/atomcontainer';
import { VisibilityConfigDef, VisibilityFlag } from '@alchemiesolns/visibility-config';
import type { AtomRecipe } from './renderAtomFromRecipe';
import { svgPaperAtoms } from './svgPaperAtoms';

/**
 * Generates atom recipes from workspace data
 *
 * Each recipe contains:
 * - atomId: Unique atom identifier
 * - atomIndex: Index in container.atoms array
 * - element: Chemical element symbol
 * - displaySymbol: Symbol to display (may include implicit H's)
 * - type: 'text' or 'carbon' (paper carbon)
 * - visibility: 'visible' or 'hidden'
 *
 * @param atoms - Array of atom models from workspace
 * @param container - Atom container with bonds and connectivity
 * @param visDefs - Visibility configuration definitions
 * @param visibleAtoms - Filtered array of visible atoms for layout calculations
 * @returns Array of atom recipes ready for rendering
 */
export function generateAtomRecipes(
  atoms: IAtomModel[],
  container: AtomContainer,
  visDefs: VisibilityConfigDef[],
  visibleAtoms: IAtomModel[]
): AtomRecipe[] {
  return atoms.map((atom) => {
    // Calculate atom index
    const atomIndex = container.atoms.findIndex(a => a.id === atom.id);

    // Determine visibility
    const visDef = visDefs.find(v => v.a === atomIndex);
    const visibility = visDef?.visibilityFlag === VisibilityFlag.Hidden ? 'hidden' : 'visible';

    // Get display symbol with implicit hydrogens
    const { symbol } = svgPaperAtoms.clapSymbolProps(atom, container, visibleAtoms);

    // Determine if this is a paper carbon
    let isPaperCarbon = svgPaperAtoms.isPaperCarbon(atom, container, visibleAtoms);

    // Override paper carbon rendering if ForceText or ForceTextHiddenLP flag is set
    if (visDef?.visibilityFlag === VisibilityFlag.ForceText ||
        visDef?.visibilityFlag === VisibilityFlag.ForceTextHiddenLP) {
      isPaperCarbon = false;
    }

    // Check if this is a group (multi-character non-element symbol)
    // Exclude hydrogen-containing symbols (handled by hydrogen offset)
    const commonElements = ['Cl', 'Br', 'Na', 'Mg', 'Ca', 'Al', 'Si', 'Fe', 'Cu', 'Zn', 'Ag', 'Sn', 'Pb'];
    const isGroup = !symbol.includes('H') && (
      symbol.length > 2 ||
      (symbol.length === 2 && !commonElements.includes(symbol))
    );

    // Determine bond direction for group alignment
    let bondDirection: 'left' | 'right' | 'default' = 'default';
    if (isGroup) {
      const bonds = container.getBondsAttachedTo(atom);
      if (bonds.length > 0) {
        // Get the first bond's neighbor position
        const bond = bonds[0];
        const neighbor = bond.a1 === atom ? bond.a2 : bond.a1;
        const neighborX = neighbor.pos?.x ?? 0;
        const atomX = atom.pos?.x ?? 0;

        // Determine bond direction
        bondDirection = neighborX < atomX ? 'left' : 'right';
      }
    }

    return {
      atomId: atom.id!,
      atomIndex,
      element: atom.symbol,
      displaySymbol: symbol,
      type: isPaperCarbon ? 'carbon' : 'text',
      visibility,
      bondDirection: isGroup ? bondDirection : undefined,
      isGroup,
    };
  });
}
